import { z } from "zod";
import {
  CLAIM_SEVERITIES,
  ISSUE_CODES,
  claimCurrencySchema,
  claimLineInputSchema,
  normalizeClaims,
  resolveSeverity,
  type ClaimLine,
  type ClaimSeverity,
} from "./claims";

/**
 * Khiếu nại nhà cung cấp (supplier claim / SCAR) — 17/09/2026.
 *
 * Louis: vấn đề của sản phẩm đôi khi đến từ vật liệu nhà cung cấp; khi đó Hisense phạt nhà cung
 * cấp, và hai hồ sơ phải nhất quán. Nên hồ sơ này dùng CÙNG danh mục lỗi với khiếu nại của khách
 * (claims.ts, phía `supplier`) và nối ngược về nguồn: khiếu nại khách, NCR, hoặc lập tay.
 *
 * Vòng đời (trigger 0085 là nơi cuối cùng chặn):
 *   Draft → Sent → Responded (nhà cung cấp Chấp nhận / Không đồng ý) → Approved → Closed
 *   Responded → Sent: đã điều chỉnh sau khi nhà cung cấp không đồng ý, gửi lại.
 *   Draft / Sent / Responded → Cancelled.
 * Số tiền DUYỆT (không vượt số đòi) mới là tiền: người duyệt phải có ô "Duyệt" (DECISION_RULES).
 */

export const SUPPLIER_CLAIM_STATUSES = ["Draft", "Sent", "Responded", "Approved", "Closed", "Cancelled"] as const;
export type SupplierClaimStatus = (typeof SUPPLIER_CLAIM_STATUSES)[number];
export const SUPPLIER_CLAIM_SOURCES = ["CustomerCase", "Ncr", "Manual"] as const;
export type SupplierClaimSource = (typeof SUPPLIER_CLAIM_SOURCES)[number];
export const SUPPLIER_RESPONSES = ["Accepted", "Disputed"] as const;
export type SupplierResponse = (typeof SUPPLIER_RESPONSES)[number];

/**
 * Hồ sơ đã SỬA sau khi nhà cung cấp phản hồi (20/09/2026): câu trả lời cũ là cho nội dung cũ, nên phải gửi lại cho nhà cung cấp
 * trước khi duyệt số tiền — không thì cổng hiện "đã chấp nhận" cạnh một nội dung nhà cung cấp chưa từng thấy. Dùng chung cho API
 * (chặn duyệt) và màn hình (ẩn nút Duyệt, đưa Gửi nhà cung cấp lên). `events` mới nhất trước, đúng thứ tự API trả.
 */
export function claimEditedAfterResponse(status: string, events: readonly { action: string }[]): boolean {
  if (status !== "Responded") return false;
  return events.find((e) => e.action === "Edited" || e.action === "Responded" || e.action === "SupplierResponded")?.action === "Edited";
}

const id = z.string().min(1).max(150);

export const supplierClaimDraftSchema = z
  .object({
    requestId: z.string().uuid(),
    supplierId: id,
    sourceKind: z.enum(SUPPLIER_CLAIM_SOURCES),
    caseId: id.nullable().default(null),
    ncrId: id.nullable().default(null),
    productId: id.nullable().default(null),
    lotNumber: z.string().trim().max(100).nullable().default(null),
    poId: id.nullable().default(null),
    issueCode: z.enum(ISSUE_CODES),
    severity: z.enum(CLAIM_SEVERITIES).nullable().default(null),
    claims: z.array(claimLineInputSchema).max(12).default([]),
    currency: claimCurrencySchema,
    description: z.string().trim().min(5).max(2000),
  })
  .superRefine((v, ctx) => {
    const add = (message: string, path: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });
    if (v.sourceKind === "CustomerCase" && (!v.caseId || v.ncrId)) add("supplier_claim_source", "caseId");
    if (v.sourceKind === "Ncr" && (!v.ncrId || v.caseId)) add("supplier_claim_source", "ncrId");
    if (v.sourceKind === "Manual" && (v.caseId || v.ncrId)) add("supplier_claim_source", "sourceKind");
    if (!resolveSeverity(v.issueCode, v.severity)) add("claim_severity", "severity");
    const claims = normalizeClaims(v.issueCode, "supplier", v.claims, v.currency);
    if (!claims.ok) add(claims.error, "claims");
  });
export type SupplierClaimInput = z.input<typeof supplierClaimDraftSchema>;

export const supplierClaimUpdateSchema = supplierClaimDraftSchema.and(
  z.object({ revision: z.number().int().positive(), reason: z.string().trim().min(5).max(1000) }),
);
export type SupplierClaimUpdate = z.input<typeof supplierClaimUpdateSchema>;

export const supplierClaimActionSchema = z
  .object({
    revision: z.number().int().positive(),
    action: z.enum(["send", "recordResponse", "approve", "close", "cancel"]),
    reason: z.string().trim().min(5).max(1000),
    /** recordResponse: câu trả lời nhà cung cấp gửi qua email / điện thoại, nhân viên ghi lại. */
    response: z.enum(SUPPLIER_RESPONSES).nullable().default(null),
    responseNote: z.string().trim().max(2000).default(""),
    /** approve: số tiền được duyệt, chuỗi thập phân tối đa 2 số lẻ. */
    agreedAmount: z.string().trim().max(20).nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.action === "recordResponse" && !v.response)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "supplier_claim_response", path: ["response"] });
    if (v.action === "recordResponse" && v.response === "Disputed" && v.responseNote.length < 5)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "supplier_claim_response", path: ["responseNote"] });
  });
export type SupplierClaimAction = z.input<typeof supplierClaimActionSchema>;

/** Nhà cung cấp tự phản hồi trên cổng — chỉ hai lựa chọn và lời giải thích, không đụng được gì khác. */
export const supplierClaimPortalResponseSchema = z
  .object({
    revision: z.number().int().positive(),
    response: z.enum(SUPPLIER_RESPONSES),
    note: z.string().trim().max(2000).default(""),
  })
  .superRefine((v, ctx) => {
    if (v.response === "Disputed" && v.note.length < 5)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "supplier_claim_response", path: ["note"] });
  });
export type SupplierClaimPortalResponse = z.input<typeof supplierClaimPortalResponseSchema>;

export const supplierClaimListSchema = z.object({
  q: z.string().trim().max(200).default(""),
  status: z.enum(SUPPLIER_CLAIM_STATUSES).optional(),
  supplierId: id.optional(),
  caseId: id.optional(),
  ncrId: id.optional(),
  page: z.coerce.number().int().positive().default(1),
});
export type SupplierClaimListInput = z.input<typeof supplierClaimListSchema>;

export interface SupplierClaimDto {
  /** Nhà cung cấp riêng tư người xem không được thấy (28/10/2026) — tên bị che, khiếu nại chỉ xem (403 `private_supplier`). */
  supplierHidden?: boolean;
  id: string;
  claimNo: string;
  supplierId: string;
  supplierName: string;
  sourceKind: SupplierClaimSource;
  caseId: string | null;
  /** Null khi người xem không thuộc phạm vi khách của hồ sơ nguồn (YC-01, 07/10/2026). */
  caseNo: string | null;
  /**
   * Luôn null từ YC-01 (07/10/2026): khiếu nại nhà cung cấp là chứng từ mua hàng / chất lượng, không mang danh tính khách — muốn
   * biết khách thì mở hồ sơ khiếu nại của khách (màn Hậu mãi tự kiểm phạm vi). Giữ trường để bản cũ của giao diện không vỡ.
   */
  customerName: string | null;
  ncrId: string | null;
  ncrNo: string | null;
  productId: string | null;
  productCode: string | null;
  lotNumber: string | null;
  poId: string | null;
  poNo: string | null;
  issueCode: string;
  severity: ClaimSeverity;
  claims: ClaimLine[];
  currency: string | null;
  claimedMinor: number;
  description: string;
  status: SupplierClaimStatus;
  sentAt: number | null;
  supplierResponse: SupplierResponse | null;
  supplierResponseNote: string;
  respondedAt: number | null;
  respondedByName: string | null;
  agreedMinor: number | null;
  approvedAt: number | null;
  approvedByName: string | null;
  /** Phiếu ghi nợ HIỆN HÀNH của khiếu nại (phiếu đã đảo thì là phiếu thay thế). Null khi số tiền duyệt bằng 0. */
  debitNoteId: string | null;
  debitNoteNo: string | null;
  debitNoteStatus: "Draft" | "Posted" | "Reversed" | null;
  revision: number;
  createdAt: number;
  createdBy: string | null;
  createdByName: string | null;
  events: { action: string; reason: string; createdAt: number; createdBy: string | null; createdByName: string | null }[];
}

/** Gợi ý nhà cung cấp cho một nguồn, kèm căn cứ để người lập tự kiểm. */
export interface SupplierSuggestionDto {
  supplierId: string;
  supplierName: string;
  /** lot: truy từ lô hàng; linkedPo: đơn mua gắn với PI; product: nhà cung cấp mặc định của sản phẩm; ncr: NCR ghi sẵn. */
  basis: "lot" | "linkedPo" | "product" | "ncr";
  productId: string | null;
  productCode: string | null;
  lotNumber: string | null;
  poId: string | null;
  poNo: string | null;
}

/**
 * Tóm tắt nguồn cho màn hình lập hồ sơ: người mua hàng có thể không được mở module Hậu mãi hay
 * Chất lượng, nên nguồn được đọc qua CHÍNH menu khiếu nại nhà cung cấp — vẫn áp phạm vi khách.
 */
export interface SupplierClaimSourceDto {
  kind: "CustomerCase" | "Ncr";
  id: string;
  no: string;
  /** Luôn null từ YC-01 (07/10/2026) — xem `SupplierClaimDto.customerName`. */
  customerName: string | null;
  issueCode: string | null;
  severity: ClaimSeverity | null;
  /**
   * Chữ điền sẵn vào mô tả gửi nhà cung cấp. Từ NCR: tiêu đề NCR. Từ khiếu nại của khách: RỖNG (YC-01) — chữ của hồ sơ khách hay
   * mang tên khách, giá bán; nhân viên đọc `caseText` rồi tự viết lại cho nhà cung cấp.
   */
  description: string;
  /** Chữ của hồ sơ khiếu nại khách (tiêu đề — ghi chú nguyên nhân), CHỈ để nhân viên tham khảo; không lưu vào hồ sơ nhà cung cấp. */
  caseText: string | null;
  eligible: boolean;
  suggestions: SupplierSuggestionDto[];
}

/**
 * Hồ sơ khiếu nại nhà cung cấp NHÌN TỪ CỔNG NHÀ CUNG CẤP — danh sách trắng.
 *
 * Không có tên khách, số khiếu nại khách, ghi chú nội bộ, người lập hay lịch sử thao tác nội bộ:
 * nhà cung cấp cần biết họ bị đòi gì và vì sao, không cần biết Hisense bán cho ai.
 * Chỉ hồ sơ đã từng được GỬI mới hiện; nháp là việc nội bộ.
 */
export interface PortalClaimDto {
  id: string;
  claimNo: string;
  status: SupplierClaimStatus;
  issueCode: string;
  severity: ClaimSeverity;
  claims: ClaimLine[];
  currency: string | null;
  claimedMinor: number;
  description: string;
  productCode: string | null;
  lotNumber: string | null;
  poNo: string | null;
  sentAt: number | null;
  supplierResponse: SupplierResponse | null;
  supplierResponseNote: string;
  respondedAt: number | null;
  agreedMinor: number | null;
  approvedAt: number | null;
  /** Số phiếu ghi nợ đã GHI SỔ — khoản trừ vào công nợ Hisense phải trả nhà cung cấp. Nháp thì chưa hiện. */
  debitNoteNo: string | null;
  revision: number;
}

/** Hồ sơ nhà cung cấp gắn với một khiếu nại khách — hiện trên trang hậu mãi. */
export interface LinkedSupplierClaimDto {
  id: string;
  claimNo: string;
  supplierName: string;
  status: SupplierClaimStatus;
  currency: string | null;
  claimedMinor: number;
  agreedMinor: number | null;
}
